// Create a separate component which will load RichTextEditor only in browser
import React, { memo, useCallback } from 'react';
import { debounce } from 'lodash';

import fileManagement from 'utils/s3';
import { stripDataUriImagesFromHtml } from 'utils/stripDataUriImagesFromHtml';
import { Box } from '@mui/material';
import { REACT_APP_CLOUDFRONT_URL } from 'config/config';

const MAX_DESCRIPTION_IMAGE_BYTES = 2_000_000;

const RichText = ({
  eventId,
  value,
  onChange,
  readOnly = true,
}: {
  eventId: number;
  value: string;
  onChange?: (html: string) => void;
  readOnly?: boolean;
}) => {
  if (typeof window !== 'undefined') {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { RichTextEditor } = require('@mantine/rte');

    const handleChange = onChange
      ? debounce((html: string) => {
          onChange(stripDataUriImagesFromHtml(html));
        }, 250)
      : undefined;

    const uploadImage = async (file: File) => {
      if (file.size > MAX_DESCRIPTION_IMAGE_BYTES) {
        return undefined;
      }
      const headerPath = encodeURIComponent(eventId) + '/description/';
      const fileKey = new Date().getTime();
      // const _fileTypeIndex = file.name.lastIndexOf('.');
      // const _fileType = file.name.slice(_fileTypeIndex);

      const response = await fileManagement.putItemWithPresignedUrl({
        key: headerPath + fileKey,
        body: file,
      });

      if (response) {
        return REACT_APP_CLOUDFRONT_URL + '/' + headerPath + fileKey;
      }
    };

    const handleImageUpload = useCallback(
      (file: File): Promise<string> => {
        if (file.size > MAX_DESCRIPTION_IMAGE_BYTES) {
          return Promise.reject(
            new Error('The image is too large. Max size is 2MB.')
          );
        }

        return uploadImage(file).then((url) => {
          if (!url) {
            throw new Error('Image upload failed.');
          }
          return url;
        });
      },
      [eventId]
    );

    return (
      <Box sx={{ maxWidth: '90vw' }}>
        <RichTextEditor
          readOnly={readOnly}
          controls={[
            ['bold', 'italic', 'underline', 'link', 'image', 'video'],
            ['h1', 'h2', 'h3'],
            ['unorderedList', 'orderedList'],
            ['alignLeft', 'alignCenter', 'alignRight'],
          ]}
          value={value}
          onChange={handleChange}
          onImageUpload={handleImageUpload}
          style={{
            minHeight: '500px',
            marginBottom: '20px',
            marginTop: '20px',
            border: readOnly ? 'none' : '1px solid grey',
            overflowWrap: 'break-word',
          }}
        />
      </Box>
    );
  }

  // Render anything as fallback on server, e.g. loader or html content without editor
  return null;
};

export default memo(RichText);
